import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneField } from "@/components/PhoneField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/lib/api-error";
import { toastApiError, userErrorMessage } from "@/lib/error-messages";
import { addContractorUser } from "@/requests/internal-contractors/add-user";
import type {
  AddContractorUserRequest,
  AddContractorUserResult,
} from "@onsemble/shared/types/internal";

const addUserSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneNumber: z.string().optional(),
  emailAddress: z
    .string()
    .email("Invalid email")
    .optional()
    .or(z.literal("")),
  role: z.enum(["org:admin", "org:member"]),
}).refine(
  (data) => !!(data.phoneNumber?.trim() || data.emailAddress?.trim()),
  {
    message: "Provide a phone number or an email address",
    path: ["phoneNumber"],
  },
);

type AddUserFormData = z.infer<typeof addUserSchema>;

interface AddUserFormProps {
  orgId: string;
  orgName: string;
  onSuccess?: () => void;
  /** Request that creates the user; defaults to the internal endpoint.
   *  The PC contractors page passes its scoped endpoint instead. */
  submitUser?: (
    orgId: string,
    data: AddContractorUserRequest,
  ) => Promise<AddContractorUserResult>;
  /** Query key invalidated after a successful add; defaults to the internal list. */
  invalidateKey?: readonly unknown[];
}

export function AddUserForm({
  orgId,
  orgName,
  onSuccess,
  submitUser = addContractorUser,
  invalidateKey = ["/api/internal/contractors"],
}: AddUserFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailAddress: "",
      role: "org:member",
    },
  });

  const mutation = useMutation({
    mutationFn: (data: AddContractorUserRequest) => submitUser(orgId, data),
    onSuccess: (result) => {
      toast({
        title: "User added",
        description: `${result.firstName} ${result.lastName} has been added to ${orgName}`,
      });
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      form.reset();
      onSuccess?.();
    },
    onError: (error) => {
      toastApiError(toast, error, "Failed to add user");
    },
  });

  const onSubmit = (data: AddUserFormData) => {
    const request: AddContractorUserRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      ...(data.phoneNumber?.trim() ? { phoneNumber: data.phoneNumber } : {}),
      ...(data.emailAddress?.trim() ? { emailAddress: data.emailAddress } : {}),
    };
    mutation.mutate(request);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      {mutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {userErrorMessage(parseApiError(mutation.error))}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name *</Label>
          <Input id="firstName" {...form.register("firstName")} placeholder="John" />
          {form.formState.errors.firstName && (
            <p className="text-sm text-red-600">{form.formState.errors.firstName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name *</Label>
          <Input id="lastName" {...form.register("lastName")} placeholder="Doe" />
          {form.formState.errors.lastName && (
            <p className="text-sm text-red-600">{form.formState.errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <PhoneField
          value={form.watch("phoneNumber") || ""}
          onChange={(value) => form.setValue("phoneNumber", value)}
        />
        {form.formState.errors.phoneNumber && (
          <p className="text-sm text-red-600">{form.formState.errors.phoneNumber.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="emailAddress">Email Address</Label>
        <Input
          id="emailAddress"
          type="email"
          {...form.register("emailAddress")}
          placeholder="john@example.com"
        />
        {form.formState.errors.emailAddress && (
          <p className="text-sm text-red-600">{form.formState.errors.emailAddress.message}</p>
        )}
        <p className="text-xs text-muted-foreground">
          At least one contact method is required.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Role *</Label>
        <Select
          value={form.watch("role")}
          onValueChange={(value) => form.setValue("role", value as "org:admin" | "org:member")}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="org:member">Member</SelectItem>
            <SelectItem value="org:admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Adding User...
          </>
        ) : (
          "Add User"
        )}
      </Button>
    </form>
  );
}
